import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Sun, Compass, BookOpen, User, ArrowLeft, Shield } from "lucide-react";
import logo from "@/assets/logo-white.png";
import { useAuth } from "@/context/AuthContext";
import { Subs } from "@/components/Subs";

const NAV = [
  { to: "/", label: "Today", icon: Sun, testid: "nav-today", end: true },
  { to: "/journey", label: "Journey", icon: Compass, testid: "nav-journey" },
  { to: "/learn", label: "Learn", icon: BookOpen, testid: "nav-learn" },
  { to: "/you", label: "You", icon: User, testid: "nav-you" },
];

export const Wordmark = ({ className = "" }) => (
  <span className={`font-serif text-ink ${className}`}><Subs>ME₀ → ME₁₁:</Subs> <span className="text-gold">Emergence</span></span>
);

export function Shell({ sub = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (sub) {
    return (
      <div className="min-h-screen bg-surface">
        <header className="sticky top-0 z-30 bg-surface/90 backdrop-blur border-b border-line">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-4">
            <button onClick={() => navigate(-1)} data-testid="back-button"
              className="inline-flex items-center gap-2 text-inkfaint hover:text-ink transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm">Back</span>
            </button>
            <div className="flex-1 text-center"><Wordmark className="text-base" /></div>
            <div className="w-16" />
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8 pb-24"><Outlet /></main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface md:flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-64 md:fixed md:inset-y-0 border-r border-line bg-panel/40 px-5 py-7">
        <img src={logo} alt="Primal Integrity" className="w-28 opacity-80 mb-8" />
        <div className="overline mb-6">ME₀ → ME₁₁ · Emergence</div>
        <nav className="flex flex-col gap-1">
          {NAV.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-full text-sm transition-colors ${
                  isActive ? "bg-golddark text-gold" : "text-inkfaint hover:text-ink hover:bg-panel"
                }`}>
              <Icon className="w-5 h-5" /> {label}
            </NavLink>
          ))}
          {user?.role === "owner" && (
            <NavLink to="/admin" data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-full text-sm transition-colors ${
                  isActive ? "bg-golddark text-gold" : "text-inkfaint hover:text-ink hover:bg-panel"
                }`}>
              <Shield className="w-5 h-5" /> Access
            </NavLink>
          )}
        </nav>
        <div className="mt-auto pt-6 text-xs text-faint">{user?.name}</div>
      </aside>

      {/* Mobile top header */}
      <header className="md:hidden sticky top-0 z-30 bg-surface/90 backdrop-blur border-b border-line h-16 flex items-center justify-center px-5">
        <img src={logo} alt="Primal Integrity" className="h-8 opacity-80" />
      </header>

      <div className="md:pl-64 flex-1">
        <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8 pb-28 md:pb-12"><Outlet /></main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-panel border-t border-line flex">
        {NAV.map(({ to, label, icon: Icon, testid, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`${testid}-mobile`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-1 py-3 text-[11px] font-semibold transition-colors ${
                isActive ? "text-gold" : "text-faint"
              }`}>
            <Icon className="w-5 h-5" /> {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
